import { useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CONFIG } from "../../data/config";
import { useFocusTrap } from "../../hooks/useFocusTrap";

const NAV_LINKS = [
  { to: "/research", label: "Research" },
  { to: "/projects", label: "Projects" },
  { to: "/publications", label: "Publications" },
  { to: "/teaching", label: "Teaching" },
  { to: "/lab", label: "Lab" },
];

const navLinkStyle = ({ isActive }) => ({
  fontSize: 15,
  fontWeight: 500,
  color: isActive ? "var(--ink-900)" : "var(--ink-700)",
  textDecoration: "none",
  paddingBottom: 4,
  borderBottom: isActive ? "2px solid var(--accent-teal)" : "2px solid transparent",
});

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const drawerRef = useRef(null);
  useFocusTrap(drawerRef, open, () => setOpen(false));

  return (
    <header className="sticky top-0 z-50 bg-white" style={{ borderBottom: "1px solid var(--border-default)" }}>
      <div className="container-site flex items-center justify-between h-16 lg:h-20" data-testid="site-nav">
        <Link to="/" className="no-underline" data-testid="nav-wordmark" style={{ textDecoration: "none" }}>
          <span className="text-h4 block" style={{ color: "var(--navy-700)" }}>Keyur Sorathia, PhD</span>
          <span className="caption block">UX Design, HCI, Virtual Reality</span>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} style={navLinkStyle} data-testid={`nav-link-${l.label.toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <NavLink to="/contact" className="btn-ghost" data-testid="nav-link-contact" style={{ fontSize: 15, fontWeight: 500, color: "var(--navy-500)", textDecoration: "none" }}>
            Contact
          </NavLink>
          <span aria-hidden="true" style={{ width: 1, height: 24, background: "var(--border-default)" }} />
          <a href={CONFIG.cvOnePage} className="link-default" data-testid="nav-cv-one-page" style={{ fontSize: 15, fontWeight: 500 }}>
            1-page CV
          </a>
          <a href={CONFIG.cvFull} className="link-default" data-testid="nav-cv-full" style={{ fontSize: 15, fontWeight: 500 }}>
            Full CV
          </a>
        </div>

        <button
          type="button"
          className="lg:hidden flex items-center justify-center"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-hamburger"
          onClick={() => setOpen(true)}
          style={{ width: 44, height: 44, background: "none", border: "none", cursor: "pointer", color: "var(--navy-700)" }}
        >
          <Menu size={24} aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto" ref={drawerRef} data-testid="nav-mobile-drawer">
          <div className="flex items-center justify-between px-5" style={{ height: 64, borderBottom: "1px solid var(--border-default)" }}>
            <span className="text-h4" style={{ color: "var(--navy-700)" }}>Keyur Sorathia, PhD</span>
            <button
              type="button"
              aria-label="Close menu"
              data-testid="nav-drawer-close"
              onClick={() => setOpen(false)}
              style={{ width: 44, height: 44, background: "none", border: "none", cursor: "pointer", color: "var(--navy-700)" }}
            >
              <X size={24} aria-hidden="true" />
            </button>
          </div>
          <nav aria-label="Mobile" className="px-5 py-6 flex flex-col gap-5">
            {[...NAV_LINKS, { to: "/contact", label: "Contact" }].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`drawer-link-${l.label.toLowerCase()}`}
                className="text-h3"
                style={{ textDecoration: "none", color: "var(--ink-900)" }}
              >
                {l.label}
              </NavLink>
            ))}
            <a href={CONFIG.cvOnePage} className="btn btn-primary w-full mt-4" data-testid="drawer-cv-one-page">One-page CV</a>
            <a href={CONFIG.cvFull} className="btn btn-primary w-full" data-testid="drawer-cv-full">Full CV</a>
            <div className="mt-4">
              <p className="body-sm m-0 mb-1"><a className="link-default" href={`mailto:${CONFIG.emailOfficial}`}>{CONFIG.emailOfficial}</a></p>
              <p className="body-sm m-0 mb-1">{CONFIG.phone}</p>
              <p className="caption m-0">{CONFIG.address}</p>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
